import { Card, Button, DatePicker } from "@devhop/ui";
import { CalendarIcon } from "lucide-react";
import { formatDateForInput } from "@/shared/lib/date-time";
import { useState, useEffect } from "react";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { config } from "@/shared/lib/config";

type DepartmentItem = LookupItem;

type AuditSummaryFilterProps = {
  filters: {
    startDate?: string;
    endDate?: string;
    departmentId?: string;
  };
  onFiltersChange: (filters: {
    startDate?: string;
    endDate?: string;
    departmentId?: string;
  }) => void;
};

export function AuditSummaryFilter({
  filters,
  onFiltersChange,
}: AuditSummaryFilterProps) {
  const [startDate, setStartDate] = useState(filters.startDate || "");
  const [endDate, setEndDate] = useState(filters.endDate || "");
  const [departmentId, setDepartmentId] = useState(filters.departmentId || "");

  const handleChange = (opts: Partial<{ startDate: string; endDate: string; departmentId: string }> = {}) => {
    const sd = opts.startDate ?? startDate;
    const ed = opts.endDate ?? endDate;
    const did = opts.departmentId ?? departmentId;
    onFiltersChange({
      startDate: sd || undefined,
      endDate: ed || undefined,
      departmentId: did || undefined,
    });
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setDepartmentId("");
    onFiltersChange({});
  };

  // Set default to current month if no dates selected
  useEffect(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = now;
    if (!startDate && !endDate && !filters.startDate && !filters.endDate) {
      setStartDate(formatDateForInput(monthStart));
      setEndDate(formatDateForInput(monthEnd));
    }
  }, []);

  return (
    <Card className="p-4" data-tourid="summary-filter">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">ຊ່ວງເວລາ:</span>
        </div>

        <DatePicker
          mode="range"
          value={{
            from: startDate ? new Date(startDate) : undefined,
            to: endDate ? new Date(endDate) : undefined,
          }}
          onChange={(range) => {
            const from: Date | undefined = Array.isArray(range)
              ? range[0]
              : range?.from;
            const to: Date | undefined = Array.isArray(range)
              ? range[1]
              : range?.to;
            const fromStr = from ? formatDateForInput(from) : "";
            const toStr = to ? formatDateForInput(to) : "";
            setStartDate(fromStr);
            setEndDate(toStr);
          }}
          placeholder="ເລືອກຊ່ວງວັນທີ"
          className="h-8 w-full sm:w-72"
        />

        <div className="min-w-[200px] max-w-[300px] flex-1">
          <InfiniteCombobox<DepartmentItem>
            value={departmentId}
            onValueChange={(v) => {
              setDepartmentId(v);
              handleChange({ departmentId: v });
            }}
            queryKey={["departments", "summary-filter"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/internal-audit/departments/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/internal-audit/departments/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            placeholder="ເລືອກພະແນກ (ທັງໝົດ)"
            clearable
          />
        </div>

        <Button variant="outline" size="sm" onClick={handleReset}>
          ລຶບການກອງ
        </Button>
      </div>
    </Card>
  );
}

